import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { 
  ChevronLeft, ChevronRight, Search, Filter, AlertTriangle,
  Edit2, Check, X, Download, CheckCircle2, Loader2,
  CheckSquare, Square, HelpCircle, MessageSquare, FileText
} from 'lucide-react';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/financialFormat';
import PDFViewerModal from './accountant/PDFViewerModal';

// Auto-approval thresholds (matching old system for 88-99% auto-approval)
const AUTO_APPROVE_THRESHOLD_ANY = 0.95;      // 95%+ → auto-approve ANY category
const AUTO_APPROVE_THRESHOLD_CLEAR = 0.90;    // 90%+ → auto-approve clear-cut categories
const AUTO_APPROVE_THRESHOLD_CLEAR_LOW = 0.85; // 85%+ → auto-approve clear-cut categories

// Clear-cut categories that can be auto-approved at lower thresholds
// These are unambiguous categories where 85%+ confidence is sufficient
const CLEAR_CUT_CATEGORIES = new Set([
  'Utilities',
  'Rent & Occupancy', 
  'Rent',
  'Payroll & Benefits',
  'Payroll',
  'Labor',
  'Insurance',
  'Bank & Financial Fees',
  'Sales Revenue',
  'Revenue',
  'Technology & Software',
  'Debt Service',
  'Fixed Costs',
  'COGS',
  'Inventory & Merchandise',  // Added - common for retail
  'Food & Beverage Cost',     // Added - common for restaurants
  'Professional Services',     // Added - unambiguous
  'Marketing & Advertising',   // Added - unambiguous
]);

// Category options matching the old system
const CATEGORIES = [
  "Revenue",
  "Payroll",
  "Fixed Costs",
  "Variable Costs",
  "COGS",
  "Rent & Occupancy",
  "Utilities",
  "Insurance",
  "Payroll & Benefits",
  "Professional Services",
  "Marketing & Advertising",
  "Technology & Software",
  "Office & Supplies",
  "Travel & Entertainment",
  "Vehicle & Transportation",
  "Inventory & Merchandise",
  "Food & Beverage Cost",
  "Manufacturing",
  "Sales Revenue",
  "Other Income",
  "Bank & Financial Fees",
  "Taxes & Licenses",
  "Debt Service",
  "Miscellaneous",
  "Transfer",
  "Owner Draw",
  "Uncategorized"
];

interface Transaction {
  transaction_date: string;
  amount: number;
  description: string;
  category: string;
  sub_category?: string;
  classification?: {
    type: string;
    confidence: number;
    auto_classified: boolean;
    reasoning?: string;
  };
  source_type?: string;
  source_file?: string;
  source_page?: number;  // PDF page number where this transaction was found
  anomaly?: boolean;
  needs_review?: boolean;
  validation_status?: 'pending' | 'approved' | 'modified' | 'rejected';
  auto_approved?: boolean;
  original_row?: number;
  raw_text?: string;
  flagged_for_client?: boolean;
  client_question?: string;
}

// Flagged transaction for client clarification
export interface FlaggedTransaction {
  id: string;
  periodKey: string;
  txnIndex: number;
  date: string;
  description: string;
  amount: number;
  category?: string;
  question: string;
}

interface Period {
  year: number;
  month?: number;
  transactions: Transaction[];
}

interface TransactionViewerProps {
  periods: Period[];
  onTransactionEdit?: (periodKey: string, txnIndex: number, updates: Partial<Transaction>) => void;
  onBulkApprove?: (transactionIds: string[]) => void;
  userId?: string;
  highlightedTransactionId?: string | null;
  onHighlightClear?: () => void;
  // Client clarification props
  flaggedTransactions?: FlaggedTransaction[];
  onFlagForClient?: (transaction: FlaggedTransaction) => void;
  onUnflagTransaction?: (transactionId: string) => void;
  // PDF viewer props
  reportId?: string;  // Required for viewing source PDFs
}

/**
 * TransactionViewer - Forensic accounting transaction table
 * 
 * Features:
 * - Pagination (100 per page)
 * - Search/filter by description, category
 * - Status filter (all, needs_review, approved)
 * - Bulk approve selected transactions
 * - Inline editing with AI learning integration
 * - Auto-approval for 95%+ confidence transactions
 * - Export to CSV
 * - Highlight and scroll to specific transaction from anomaly detection
 */
export default function TransactionViewer({ 
  periods, 
  onTransactionEdit,
  onBulkApprove,
  userId,
  highlightedTransactionId,
  onHighlightClear,
  flaggedTransactions = [],
  onFlagForClient,
  onUnflagTransaction,
  reportId
}: TransactionViewerProps) {
  // State - Initialize filter from localStorage for persistence during review sessions
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFlagModal, setShowFlagModal] = useState<string | null>(null);
  const [flagQuestion, setFlagQuestion] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('txnViewer_filterCategory') || 'all';
    }
    return 'all';
  });
  const [filterStatus, setFilterStatus] = useState<'all' | 'needs_review' | 'approved'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('txnViewer_filterStatus');
      if (saved === 'needs_review' || saved === 'approved') return saved;
    }
    return 'all';
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingTxn, setEditingTxn] = useState<{ id: string; periodKey: string; index: number } | null>(null);
  const [editValues, setEditValues] = useState<Partial<Transaction>>({});
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [recordingLearning, setRecordingLearning] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [categoryDropdownId, setCategoryDropdownId] = useState<string | null>(null); // For inline category edit
  
  // PDF Viewer state for viewing source documents
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerFile, setPdfViewerFile] = useState('');
  const [pdfViewerPage, setPdfViewerPage] = useState(1);
  const [pdfViewerContext, setPdfViewerContext] = useState<{date: string; description: string; amount: number} | undefined>(undefined);
  
  const ITEMS_PER_PAGE = 100;
  const BULK_CONFIRM_THRESHOLD = 50; // Show confirmation when approving >50 items
  
  // Persist filter state to localStorage
  useEffect(() => {
    localStorage.setItem('txnViewer_filterCategory', filterCategory);
  }, [filterCategory]);
  
  useEffect(() => {
    localStorage.setItem('txnViewer_filterStatus', filterStatus);
  }, [filterStatus]);
  
  // Ref for scrolling to highlighted transaction
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryDropdownId && !(e.target as Element).closest('.category-dropdown-container')) {
        setCategoryDropdownId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [categoryDropdownId]);
  
  // Flatten all transactions from all periods with unique IDs
  const allTransactions = useMemo(() => {
    const txns: (Transaction & { 
      id: string; 
      periodKey: string; 
      periodIndex: number; 
      txnIndex: number;
      isAutoApproved: boolean;
      needsReview: boolean;
    })[] = [];
    
    periods.forEach((period, periodIndex) => {
      const periodKey = period.month 
        ? `${period.year}-${String(period.month).padStart(2, '0')}`
        : `${period.year}`;
        
      (period.transactions || []).forEach((txn, txnIndex) => {
        const confidence = txn.classification?.confidence || 0;
        const category = txn.category || '';
        
        // Tiered auto-approval logic (matching old system for 88-99% auto-approval):
        // 1. 95%+ confidence → auto-approve ANY category
        // 2. 90%+ confidence → auto-approve if clear-cut category
        // 3. 85%+ confidence → auto-approve if clear-cut category
        let shouldAutoApprove = false;
        
        if (txn.auto_approved === true || txn.validation_status === 'approved' || txn.validation_status === 'modified') {
          shouldAutoApprove = true;
        } else if (confidence >= AUTO_APPROVE_THRESHOLD_ANY) {
          shouldAutoApprove = true;
        } else if (confidence >= AUTO_APPROVE_THRESHOLD_CLEAR && CLEAR_CUT_CATEGORIES.has(category)) {
          shouldAutoApprove = true;
        } else if (confidence >= AUTO_APPROVE_THRESHOLD_CLEAR_LOW && CLEAR_CUT_CATEGORIES.has(category)) {
          shouldAutoApprove = true;
        }
        
        const isAutoApproved = shouldAutoApprove;
        
        // Needs review if NOT auto-approved
        const needsReview = !isAutoApproved;
        
        txns.push({
          ...txn,
          id: `${periodKey}-${txnIndex}`,
          periodKey,
          periodIndex,
          txnIndex,
          isAutoApproved,
          needsReview,
          // Set validation_status based on confidence if not already set
          validation_status: txn.validation_status || (isAutoApproved ? 'approved' : 'pending'),
        });
      });
    });
    
    // Sort by date descending (most recent first)
    return txns.sort((a, b) => 
      new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime()
    );
  }, [periods]);
  
  // Handle scrolling to highlighted transaction when it changes
  useEffect(() => {
    if (!highlightedTransactionId) return;
    
    // Find the transaction index
    const txnIndex = allTransactions.findIndex(t => t.id === highlightedTransactionId);
    if (txnIndex === -1) {
      console.log('Transaction not found:', highlightedTransactionId);
      return;
    }
    
    // Calculate which page the transaction is on
    const targetPage = Math.floor(txnIndex / ITEMS_PER_PAGE) + 1;
    
    // Clear filters to ensure transaction is visible
    setFilterCategory('all');
    setFilterStatus('all');
    setSearchQuery('');
    
    // Navigate to the correct page
    setCurrentPage(targetPage);
    
    // Scroll to the row after a short delay for render
    setTimeout(() => {
      const row = document.querySelector(`[data-txn-id="${highlightedTransactionId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Flash highlight effect
        row.classList.add('animate-pulse', 'ring-2', 'ring-[var(--color-brand)]', 'ring-offset-2');
        setTimeout(() => {
          row.classList.remove('animate-pulse', 'ring-2', 'ring-[var(--color-brand)]', 'ring-offset-2');
          if (onHighlightClear) onHighlightClear();
        }, 3000);
      }
    }, 100);
  }, [highlightedTransactionId, allTransactions]);
  
  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allTransactions.forEach(txn => {
      if (txn.category) cats.add(txn.category);
    });
    return Array.from(cats).sort();
  }, [allTransactions]);
  
  // Apply filters
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(txn => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          txn.description?.toLowerCase().includes(query) ||
          txn.category?.toLowerCase().includes(query) ||
          txn.source_file?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Category filter
      if (filterCategory !== 'all' && txn.category !== filterCategory) {
        return false;
      }
      
      // Status filter
      if (filterStatus === 'needs_review' && !txn.needsReview) {
        return false;
      }
      if (filterStatus === 'approved' && txn.needsReview) {
        return false;
      }
      
      return true;
    });
  }, [allTransactions, searchQuery, filterCategory, filterStatus]);
  
  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Stats
  const stats = useMemo(() => {
    const needsReview = allTransactions.filter(t => t.needsReview).length;
    const autoApproved = allTransactions.filter(t => t.isAutoApproved).length;
    const lowConfidence = allTransactions.filter(t => (t.classification?.confidence || 0) < 0.7).length;
    const totalAmount = allTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
    return { needsReview, autoApproved, lowConfidence, totalAmount };
  }, [allTransactions]);
  
  // Format date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  // Get confidence color based on tiered thresholds.
  // Returns inline style values (token-based) so themes are honored:
  // approved → status-done (green); needs-review / low-confidence → brand (orange).
  const getConfidenceColor = (confidence: number, category: string): React.CSSProperties => {
    const isClearCut = CLEAR_CUT_CATEGORIES.has(category);

    if (confidence >= AUTO_APPROVE_THRESHOLD_ANY) {
      return { color: 'var(--color-verified)', background: 'transparent', borderColor: 'var(--color-verified)' }; // Auto-approved (any category)
    }
    if (confidence >= AUTO_APPROVE_THRESHOLD_CLEAR && isClearCut) {
      return { color: 'var(--color-verified)', background: 'transparent', borderColor: 'var(--color-verified)' }; // Auto-approved (clear-cut)
    }
    if (confidence >= AUTO_APPROVE_THRESHOLD_CLEAR_LOW && isClearCut) {
      return { color: 'var(--color-verified)', background: 'transparent', borderColor: 'var(--color-verified)' }; // Auto-approved (clear-cut)
    }
    if (confidence >= 0.7) {
      return { color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }; // Needs review
    }
    return { color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }; // Low confidence
  };
  
  // Get status badge
  const getStatusBadge = (txn: typeof allTransactions[0]) => {
    if (txn.validation_status === 'approved' || txn.isAutoApproved) {
      return (
        <span
          className="cs-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: 'transparent', color: 'var(--color-verified)' }}
        >
          <CheckCircle2 className="w-3 h-3" />
          {txn.isAutoApproved && !txn.validation_status ? 'Auto' : 'Approved'}
        </span>
      );
    }
    if (txn.validation_status === 'modified') {
      return (
        <span
          className="cs-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
        >
          <Edit2 className="w-3 h-3" />
          Modified
        </span>
      );
    }
    if (txn.needsReview) {
      return (
        <span
          className="cs-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
          style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
        >
          <AlertTriangle className="w-3 h-3" />
          Review
        </span>
      );
    }
    return <span className="cs-meta" style={{ color: 'var(--color-text-ghost)' }}>—</span>;
  };
  
  // Handle viewing source PDF at specific page
  const handleViewSourcePage = useCallback((txn: typeof allTransactions[0]) => {
    if (txn.source_file) {
      setPdfViewerFile(txn.source_file);
      setPdfViewerPage(txn.source_page || 1);
      setPdfViewerContext({
        date: txn.transaction_date,
        description: txn.description,
        amount: txn.amount
      });
      setPdfViewerOpen(true);
    }
  }, []);
  
  // Selection handlers
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  
  const selectAll = useCallback(() => {
    // Only select transactions that need review on current page
    const reviewableIds = paginatedTransactions
      .filter(t => t.needsReview)
      .map(t => t.id);
    setSelectedIds(new Set(reviewableIds));
  }, [paginatedTransactions]);
  
  const selectNone = useCallback(() => {
    setSelectedIds(new Set());
  }, []);
  
  // Bulk approve handler with confirmation for large selections
  const handleBulkApproveClick = useCallback(() => {
    if (selectedIds.size === 0) return;
    
    // Show confirmation dialog for large bulk operations
    if (selectedIds.size > BULK_CONFIRM_THRESHOLD) {
      setShowBulkConfirm(true);
    } else {
      executeBulkApprove();
    }
  }, [selectedIds.size]);
  
  const executeBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;
    
    setShowBulkConfirm(false);
    setBulkActionLoading(true);
    try {
      // Find the actual transaction data for selected IDs
      const selectedTxns = allTransactions.filter(t => selectedIds.has(t.id));
      
      // Log for observability
      console.log(`📊 Bulk approve: ${selectedTxns.length} transactions`);
      
      // Update each transaction's status
      selectedTxns.forEach(txn => {
        if (onTransactionEdit) {
          onTransactionEdit(txn.periodKey, txn.txnIndex, {
            validation_status: 'approved',
            needs_review: false,
          });
        }
      });
      
      // Call bulk approve callback if provided
      if (onBulkApprove) {
        onBulkApprove(Array.from(selectedIds));
      }
      
      // Clear selection
      setSelectedIds(new Set());
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, allTransactions, onTransactionEdit, onBulkApprove]);
  
  // Edit handlers
  const startEdit = (txn: typeof allTransactions[0]) => {
    setEditingTxn({ id: txn.id, periodKey: txn.periodKey, index: txn.txnIndex });
    setEditValues({
      category: txn.category,
      description: txn.description,
    });
  };
  
  const saveEdit = useCallback(async () => {
    if (!editingTxn) return;
    
    const txn = allTransactions.find(t => t.id === editingTxn.id);
    if (!txn) return;
    
    const originalCategory = txn.category;
    const newCategory = editValues.category;
    
    // Record AI learning if category changed
    if (originalCategory !== newCategory && userId) {
      setRecordingLearning(true);
      try {
        await api.post('/api/v1/cleanup/learnings/record', {
          user_id: userId,
          description: txn.description,
          payee: null,
          original_category: originalCategory,
          corrected_category: newCategory,
          amount: txn.amount,
          transaction_id: txn.id,
          job_id: null,
        }, { showErrorToast: false });
        console.log('📚 AI Learning recorded:', txn.description, '->', newCategory);
      } catch (err) {
        console.error('Failed to record learning:', err);
      } finally {
        setRecordingLearning(false);
      }
    }
    
    // Update transaction
    if (onTransactionEdit) {
      onTransactionEdit(editingTxn.periodKey, editingTxn.index, {
        ...editValues,
        validation_status: 'modified',
        needs_review: false,
      });
    }
    
    setEditingTxn(null);
    setEditValues({});
  }, [editingTxn, editValues, allTransactions, onTransactionEdit, userId]);
  
  const cancelEdit = () => {
    setEditingTxn(null);
    setEditValues({});
  };
  
  // Quick category change (click on category to open dropdown)
  const handleQuickCategoryChange = useCallback(async (txn: typeof allTransactions[0], newCategory: string) => {
    const originalCategory = txn.category;
    
    // Close dropdown
    setCategoryDropdownId(null);
    
    // Skip if same category
    if (originalCategory === newCategory) return;
    
    // Record AI learning if category changed
    if (userId) {
      try {
        await api.post('/api/v1/cleanup/learnings/record', {
          user_id: userId,
          description: txn.description,
          payee: null,
          original_category: originalCategory,
          corrected_category: newCategory,
          amount: txn.amount,
          transaction_id: txn.id,
          job_id: null,
        }, { showErrorToast: false });
        console.log('📚 AI Learning recorded:', txn.description, '->', newCategory);
      } catch (err) {
        console.error('Failed to record learning:', err);
      }
    }
    
    // Update transaction
    if (onTransactionEdit) {
      onTransactionEdit(txn.periodKey, txn.txnIndex, {
        category: newCategory,
        validation_status: 'modified',
        needs_review: false,
      });
    }
  }, [allTransactions, onTransactionEdit, userId]);
  
  // Approve single transaction
  const approveSingle = useCallback((txn: typeof allTransactions[0]) => {
    if (onTransactionEdit) {
      onTransactionEdit(txn.periodKey, txn.txnIndex, {
        validation_status: 'approved',
        needs_review: false,
      });
    }
  }, [onTransactionEdit]);
  
  // Flag transaction for client clarification
  const handleFlagForClient = useCallback((txn: typeof allTransactions[0], question: string) => {
    if (onFlagForClient) {
      onFlagForClient({
        id: txn.id,
        periodKey: txn.periodKey,
        txnIndex: txn.txnIndex,
        date: txn.transaction_date,
        description: txn.description,
        amount: txn.amount,
        category: txn.category,
        question: question || `What was the purpose of this ${txn.amount >= 0 ? 'income' : 'expense'}?`
      });
      
      // Update transaction to mark as flagged
      if (onTransactionEdit) {
        onTransactionEdit(txn.periodKey, txn.txnIndex, {
          flagged_for_client: true,
          client_question: question
        });
      }
    }
    setShowFlagModal(null);
    setFlagQuestion('');
  }, [onFlagForClient, onTransactionEdit]);

  // Check if transaction is flagged
  const isTransactionFlagged = useCallback((txnId: string) => {
    return flaggedTransactions.some(f => f.id === txnId);
  }, [flaggedTransactions]);
  
  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Description', 'Amount', 'Category', 'Source', 'Confidence', 'Status'];
    const rows = filteredTransactions.map(txn => [
      txn.transaction_date,
      `"${(txn.description || '').replace(/"/g, '""')}"`,
      txn.amount,
      txn.category || '',
      txn.source_file || '',
      txn.classification?.confidence || '',
      txn.isAutoApproved ? 'Auto-Approved' : (txn.needsReview ? 'Needs Review' : 'Approved')
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  
  if (allTransactions.length === 0) {
    return (
      <div
        className="rounded-lg p-8 text-center"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        <p className="cs-body" style={{ color: 'var(--color-text-muted)' }}>No individual transactions found in the uploaded data.</p>
        <p className="cs-meta mt-2">
          This may occur with summarized P&L statements. For transaction-level detail, upload bank statements or detailed transaction exports.
        </p>
      </div>
    );
  }
  
  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
    >
      {/* Header */}
      <div
        className="px-6 py-4"
        style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-sub)' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="cs-h2">Transaction Detail</h2>
            <p className="cs-meta">
              {allTransactions.length.toLocaleString()} transactions • Forensic accounting view
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Stats badges */}
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
            >
              <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
              <span className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>{stats.autoApproved} auto-approved</span>
            </div>
            {stats.needsReview > 0 && (
              <div
                className="cs-chip flex items-center gap-1 px-3 py-1 rounded-full"
                style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
              >
                <AlertTriangle className="w-4 h-4" />
                {stats.needsReview} need review
              </div>
            )}
            {flaggedTransactions.length > 0 && (
              <div
                className="cs-chip flex items-center gap-1 px-3 py-1 rounded-full"
                style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
              >
                <MessageSquare className="w-4 h-4" />
                {flaggedTransactions.length} flagged for client
              </div>
            )}
            <button
              onClick={exportToCSV}
              className="cs-body flex items-center gap-1 px-3 py-1 rounded-lg transition"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>
      
      {/* Bulk Action Bar - shows when items selected */}
      {selectedIds.size > 0 && (
        <div
          className="px-6 py-3 flex items-center justify-between"
          style={{ background: 'var(--color-needs-human-tint)', borderBottom: '1px solid var(--color-needs-human-tint-bd)' }}
        >
          <div className="flex items-center gap-3">
            <span className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
              {selectedIds.size} transaction{selectedIds.size > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={selectNone}
              className="cs-meta underline"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkApproveClick}
              disabled={bulkActionLoading}
              className="cs-body flex items-center gap-2 px-4 py-2 rounded-lg transition disabled:opacity-50"
              style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)', fontWeight: 500 }}
            >
              {bulkActionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Approve Selected
            </button>
          </div>
        </div>
      )}
      
      {/* Bulk Approve Confirmation Modal */}
      {showBulkConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div
            className="rounded-lg p-6 max-w-md mx-4"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          >
            <h3 className="cs-h3 mb-2">
              Confirm Bulk Approval
            </h3>
            <p className="cs-body mb-4">
              You are about to approve <strong>{selectedIds.size} transactions</strong>.
              This action will mark all selected items as reviewed and approved.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkConfirm(false)}
                className="cs-body px-4 py-2 rounded-lg transition"
                style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkApprove}
                disabled={bulkActionLoading}
                className="cs-body px-4 py-2 rounded-lg transition flex items-center gap-2"
                style={{ background: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
              >
                {bulkActionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Approve {selectedIds.size} Items
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div
        className="p-4 flex flex-wrap gap-4 items-center"
        style={{ background: 'var(--color-bg-base)', borderBottom: '1px solid var(--color-border-sub)' }}
      >
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          <input
            type="text"
            placeholder="Search descriptions, categories..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="cs-body w-full pl-10 pr-4 py-2 rounded-lg"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
          />
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
            className="cs-body rounded-lg px-3 py-2"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Status filter */}
        <div
          className="flex items-center gap-1 rounded-lg overflow-hidden"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
        >
          <button
            onClick={() => { setFilterStatus('all'); setCurrentPage(1); }}
            className="cs-body px-3 py-2 transition"
            style={
              filterStatus === 'all'
                ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }
                : { background: 'transparent', color: 'var(--color-text-muted)', fontWeight: 500 }
            }
          >
            All
          </button>
          <button
            onClick={() => { setFilterStatus('needs_review'); setCurrentPage(1); }}
            className="cs-body px-3 py-2 transition flex items-center gap-1"
            style={
              filterStatus === 'needs_review'
                ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }
                : { background: 'transparent', color: 'var(--color-text-muted)', fontWeight: 500 }
            }
          >
            <AlertTriangle className="w-3 h-3" />
            Needs Review
          </button>
          <button
            onClick={() => { setFilterStatus('approved'); setCurrentPage(1); }}
            className="cs-body px-3 py-2 transition flex items-center gap-1"
            style={
              filterStatus === 'approved'
                ? { background: 'var(--color-verified)', color: 'var(--color-on-brand)', fontWeight: 500 }
                : { background: 'transparent', color: 'var(--color-text-muted)', fontWeight: 500 }
            }
          >
            <CheckCircle2 className="w-3 h-3" />
            Approved
          </button>
        </div>

        {/* Results count */}
        <div className="cs-meta">
          Showing {filteredTransactions.length.toLocaleString()} of {allTransactions.length.toLocaleString()}
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-sub)' }}>
            <tr>
              <th className="cs-label text-left py-3 px-4 w-12">
                <button
                  onClick={selectedIds.size > 0 ? selectNone : selectAll}
                  className="p-1 rounded"
                  style={{ background: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  title={selectedIds.size > 0 ? "Deselect all" : "Select all needing review"}
                >
                  {selectedIds.size > 0 ? (
                    <CheckSquare className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                  ) : (
                    <Square className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                  )}
                </button>
              </th>
              <th className="cs-label text-left py-3 px-4">Date</th>
              <th className="cs-label text-left py-3 px-4">Description</th>
              <th className="cs-label text-right py-3 px-4">Amount</th>
              <th className="cs-label text-left py-3 px-4">Category</th>
              <th className="cs-label text-center py-3 px-4">Confidence</th>
              <th className="cs-label text-center py-3 px-4">Status</th>
              <th className="cs-label text-center py-3 px-4">Source</th>
              <th className="cs-label text-center py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.map((txn) => {
              const isEditing = editingTxn?.id === txn.id;
              const confidence = txn.classification?.confidence || 0;
              const isSelected = selectedIds.has(txn.id);
              const isHighlighted = highlightedTransactionId === txn.id;
              const isFlagged = isTransactionFlagged(txn.id);
              
              return (
                <React.Fragment key={txn.id}>
                <tr
                  data-txn-id={txn.id}
                  className="transition"
                  style={{
                    borderBottom: '1px solid var(--color-border-faint)',
                    background: isHighlighted
                      ? 'var(--color-needs-human-tint)'
                      : isSelected
                      ? 'var(--color-needs-human-tint)'
                      : isFlagged
                      ? 'var(--color-needs-human-tint)'
                      : txn.needsReview
                      ? 'var(--color-bg-elevated)'
                      : 'transparent',
                    borderLeft: isFlagged ? '2px solid var(--color-brand)' : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (!isHighlighted && !isSelected && !isFlagged && !txn.needsReview) {
                      e.currentTarget.style.background = 'var(--color-bg-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isHighlighted && !isSelected && !isFlagged && !txn.needsReview) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {/* Checkbox */}
                  <td className="py-3 px-4">
                    {txn.needsReview && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(txn.id)}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: 'var(--color-brand)', borderColor: 'var(--color-border)' }}
                      />
                    )}
                  </td>

                  {/* Date */}
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span className="cs-figure">{formatDate(txn.transaction_date)}</span>
                  </td>

                  {/* Description */}
                  <td className="py-3 px-4 max-w-xs">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editValues.description || ''}
                        onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                        className="cs-body w-full px-2 py-1 rounded"
                        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                      />
                    ) : (
                      <span className="cs-body line-clamp-2" style={{ color: 'var(--color-text-secondary)' }} title={txn.description}>
                        {txn.description || '—'}
                      </span>
                    )}
                  </td>

                  {/* Amount */}
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <span className="cs-figure" style={{ color: txn.amount >= 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)', fontWeight: 500 }}>
                      {formatCurrency(txn.amount, { cents: true })}
                    </span>
                  </td>

                  {/* Category - Click to edit */}
                  <td className="py-3 px-4 relative category-dropdown-container">
                    <button
                      onClick={() => setCategoryDropdownId(categoryDropdownId === txn.id ? null : txn.id)}
                      className="cs-chip inline-flex items-center gap-1 px-2 py-1 rounded transition cursor-pointer group"
                      style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-needs-human-tint)'; e.currentTarget.style.color = 'var(--color-needs-human-text)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-elevated)'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
                      title="Click to change category"
                    >
                      {txn.category || 'Uncategorized'}
                      <svg className={`w-3 h-3 transition-transform ${categoryDropdownId === txn.id ? 'rotate-180' : ''}`} style={{ color: 'var(--color-text-dim)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Category Dropdown */}
                    {categoryDropdownId === txn.id && (
                      <div
                        className="absolute z-20 mt-1 left-0 w-56 rounded-lg max-h-60 overflow-y-auto"
                        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
                      >
                        {CATEGORIES.map(cat => (
                          <button
                            key={cat}
                            onClick={() => handleQuickCategoryChange(txn, cat)}
                            className="cs-body w-full text-left px-3 py-2 transition"
                            style={
                              cat === txn.category
                                ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', fontWeight: 500 }
                                : { background: 'transparent', color: 'var(--color-text-secondary)' }
                            }
                            onMouseEnter={(e) => { if (cat !== txn.category) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                            onMouseLeave={(e) => { if (cat !== txn.category) e.currentTarget.style.background = 'transparent'; }}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Confidence */}
                  <td className="py-3 px-4 text-center">
                    <span
                      className="cs-figure inline-block px-2 py-1 rounded border"
                      style={getConfidenceColor(confidence, txn.category || '')}
                    >
                      {Math.round(confidence * 100)}%
                    </span>
                  </td>
                  
                  {/* Status */}
                  <td className="py-3 px-4 text-center">
                    {getStatusBadge(txn)}
                  </td>
                  
                  {/* Source - View PDF page */}
                  <td className="py-3 px-4 text-center">
                    {txn.source_page ? (
                      <button
                        onClick={() => handleViewSourcePage(txn)}
                        className="cs-chip inline-flex items-center gap-1 hover:underline cursor-pointer"
                        style={{ color: 'var(--color-needs-human-text)' }}
                        title={`View source PDF page ${txn.source_page}${txn.source_file ? ` (${txn.source_file})` : ''}`}
                        data-testid={`view-source-${txn.id}`}
                      >
                        <FileText size={12} /> p.{txn.source_page}
                      </button>
                    ) : txn.source_file ? (
                      <button
                        onClick={() => handleViewSourcePage(txn)}
                        className="cs-chip"
                        style={{ color: 'var(--color-text-dim)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-needs-human-text)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
                        title={`View source: ${txn.source_file}`}
                      >
                        <FileText size={12} />
                      </button>
                    ) : (
                      <span style={{ color: 'var(--color-text-ghost)' }}>—</span>
                    )}
                  </td>
                  
                  {/* Actions */}
                  <td className="py-3 px-4 text-center">
                    {isEditing ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={saveEdit}
                          disabled={recordingLearning}
                          className="p-1 rounded disabled:opacity-50"
                          style={{ color: 'var(--color-verified)' }}
                          title="Save"
                        >
                          {recordingLearning ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 rounded"
                          style={{ color: 'var(--color-text-muted)' }}
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : txn.needsReview ? (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => approveSingle(txn)}
                          className="p-1.5 rounded transition"
                          style={{ color: 'var(--color-verified)' }}
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => startEdit(txn)}
                          className="p-1.5 rounded transition"
                          style={{ color: 'var(--color-needs-human-text)' }}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {onFlagForClient && (
                          isTransactionFlagged(txn.id) ? (
                            <button
                              onClick={() => onUnflagTransaction?.(txn.id)}
                              className="p-1.5 rounded transition"
                              style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' }}
                              title="Flagged for client - click to unflag"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setShowFlagModal(txn.id);
                                setFlagQuestion(`What was the purpose of this ${txn.amount >= 0 ? 'income' : 'expense'}?`);
                              }}
                              className="p-1.5 rounded transition"
                              style={{ color: 'var(--color-text-dim)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-needs-human-text)'; e.currentTarget.style.background = 'var(--color-needs-human-tint)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; e.currentTarget.style.background = 'transparent'; }}
                              title="Ask client for clarification"
                            >
                              <HelpCircle className="w-4 h-4" />
                            </button>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => startEdit(txn)}
                          className="p-1 rounded transition"
                          style={{ color: 'var(--color-text-dim)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-needs-human-text)'; e.currentTarget.style.background = 'var(--color-needs-human-tint)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; e.currentTarget.style.background = 'transparent'; }}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {onFlagForClient && (
                          isTransactionFlagged(txn.id) ? (
                            <button
                              onClick={() => onUnflagTransaction?.(txn.id)}
                              className="p-1.5 rounded transition"
                              style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)' }}
                              title="Flagged for client - click to unflag"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setShowFlagModal(txn.id);
                                setFlagQuestion(`What was the purpose of this ${txn.amount >= 0 ? 'income' : 'expense'}?`);
                              }}
                              className="p-1 rounded transition"
                              style={{ color: 'var(--color-text-ghost)' }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-needs-human-text)'; e.currentTarget.style.background = 'var(--color-needs-human-tint)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-ghost)'; e.currentTarget.style.background = 'transparent'; }}
                              title="Ask client for clarification"
                            >
                              <HelpCircle className="w-4 h-4" />
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                
                {/* Flag Modal - inline below the row */}
                {showFlagModal === txn.id && (
                  <tr style={{ background: 'var(--color-needs-human-tint)', borderLeft: '4px solid var(--color-brand)' }}>
                    <td colSpan={8} className="px-4 py-3">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <label className="cs-label block mb-1" style={{ color: 'var(--color-needs-human-text)' }}>
                            What should we ask the client about this transaction?
                          </label>
                          <input
                            type="text"
                            value={flagQuestion}
                            onChange={(e) => setFlagQuestion(e.target.value)}
                            placeholder="e.g., Was this expense for equipment or consulting services?"
                            className="cs-body w-full px-3 py-2 rounded-lg"
                            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' }}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && flagQuestion.trim()) {
                                handleFlagForClient(txn, flagQuestion);
                              }
                              if (e.key === 'Escape') {
                                setShowFlagModal(null);
                                setFlagQuestion('');
                              }
                            }}
                          />
                          <p className="cs-meta mt-1">
                            Transaction: {txn.description} ({formatCurrency(txn.amount, { cents: true })})
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleFlagForClient(txn, flagQuestion)}
                            disabled={!flagQuestion.trim()}
                            className="cs-body px-4 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
                          >
                            Flag for Client
                          </button>
                          <button
                            onClick={() => {
                              setShowFlagModal(null);
                              setFlagQuestion('');
                            }}
                            className="cs-body px-4 py-2 rounded-lg transition"
                            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontWeight: 500 }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ background: 'var(--color-bg-base)', borderTop: '1px solid var(--color-border-sub)' }}
        >
          <div className="cs-meta">
            Page {currentPage} of {totalPages} •
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredTransactions.length)} of {filteredTransactions.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className="cs-figure px-3 py-1 rounded"
                    style={
                      currentPage === pageNum
                        ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }
                    }
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div
        className="cs-meta px-6 py-3"
        style={{ background: 'var(--color-bg-base)', borderTop: '1px solid var(--color-border-sub)' }}
      >
        <p>
          <strong style={{ color: 'var(--color-text-secondary)' }}>Smart Auto-approval:</strong> ≥95% confidence auto-approves any category.
          ≥85% confidence auto-approves clear-cut categories (Rent, Utilities, Payroll, Revenue, Insurance, etc.).
          Edits are recorded to improve future AI classification.
        </p>
      </div>
      
      {/* PDF Viewer Modal */}
      {reportId && (
        <PDFViewerModal
          isOpen={pdfViewerOpen}
          reportId={reportId}
          sourceFile={pdfViewerFile}
          sourcePage={pdfViewerPage}
          transactionContext={pdfViewerContext ? {
            date: pdfViewerContext.date,
            vendor: pdfViewerContext.description,
            amount: pdfViewerContext.amount
          } : undefined}
          onClose={() => setPdfViewerOpen(false)}
        />
      )}
    </div>
  );
}
